import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ButtonGroup, StackDirection, Text, Tooltip } from "@chakra-ui/react";

import BackToTopFAB from "components/BackToTopFAB";
import EmptyWithClearButton from "components/EmptyWithClearButton";
import FileDownloadLink from "components/tableColumns/FileDownloadLink";
import { BACK_TO_TOP_MIN_LINES } from "components/SortableTable/SortableDynamicTable";
import { SelectFormInput, TextFormInput } from "components/inputs";
import SubmitButton from "components/SubmitButton";
import CancelButtonAlert from "components/CancelButtonAlert";
import { useRequestParams } from "contexts/RequestParamsProvider";
import { EditFormInputs } from "forms/EditOrderForm";
import useOrderFiles from "hooks/api/orders/useOrderFiles";
import usePermission from "hooks/usePermission";
import useQueryParam, { QUERY_STRING_KEYS } from "hooks/useQueryParam";
import { useOrderAddresses } from "hooks/api/orders/useOrders";
import { SHIPPING_STATUS } from "services/OrderService";
import { ORDERS } from "util/featureFlags";
import { DEFAULT_EMPTY_VALUE, valueOr } from "util/statHelpers";
import { mobileStyleBreakpoint } from "util/styleHelpers";
import EditButton from "./components/EditButton";
import OrdersTable from "./OrdersTable";

const setShippingStatusTextColor = (shippingStatus: ContractTermValue) => {
  const status = {
    canceled: "warningRed",
    shipped: "inherit",
    notShipped: "orange.400",
  };

  switch (shippingStatus) {
    case SHIPPING_STATUS.CANCELED:
      return (
        <Text color={status.canceled} fontWeight="semibold" textStyle="body2">
          {shippingStatus}
        </Text>
      );
    case SHIPPING_STATUS.SHIPPED:
      return (
        <Text color={status.shipped} fontWeight="semibold" textStyle="body2">
          {shippingStatus}
        </Text>
      );
    case SHIPPING_STATUS.NOT_SHIPPED:
      return (
        <Text color={status.notShipped} fontWeight="semibold" textStyle="body2">
          {shippingStatus}
        </Text>
      );
    default:
      throw Error("Shipping Status Not Found");
  }
};

const headingDirection = mobileStyleBreakpoint<StackDirection>(
  "column",
  "row"
) as StackDirection;
const saveButtonVariant = mobileStyleBreakpoint("solid", "link");
const editOrderInputWidth = mobileStyleBreakpoint("132px", "106px");

const OrdersList = ({ orders }: { orders: OrderLineItem[] }): ReactElement => {
  const [rowData, setRowData] = useState<OrderLineItem>();
  const {
    formState: { isDirty, isSubmitting, isSubmitSuccessful },
    reset,
  } = useFormContext<EditFormInputs>();

  const [selectedTabIndex] = useQueryParam(QUERY_STRING_KEYS.SELECTED_TAB_KEY);

  useEffect(() => {
    setRowData(undefined);
  }, [selectedTabIndex]);

  useEffect(() => {
    return () => {
      setRowData(undefined);
    };
  }, []);

  const hasEditOrderPermission = usePermission(ORDERS.EDIT);

  const { searchTerm, hasSelectedFilters, clearFilters, clearSearchTerm } =
    useRequestParams();

  const { data: validShipTo, isLoading: ValidShipToIsLoading } =
    useOrderAddresses(
      rowData?.contractNumber as number,
      rowData?.shipFrom as string,
      rowData?.mode?.code,
      rowData?.salesTermsCode
    );

  const shipToSelectOptions = useMemo(
    () =>
      validShipTo?.map((shipTo) => ({
        label: shipTo.code,
        value: shipTo.code,
      })) ?? [],
    [validShipTo]
  );

  const closeEditRow = useCallback(() => {
    setRowData(undefined);
    reset();
  }, [reset]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      closeEditRow();
    }
  }, [reset, isSubmitSuccessful, closeEditRow]);

  const { mutateAsync } = useOrderFiles();

  const ordersTableEntries = useMemo(
    () =>
      orders?.map((value) => {
        const canEdit =
          hasEditOrderPermission && value.notShipped && !value.isCanceled;
        const inEditMode = value.docEntry === rowData?.docEntry;
        const editPoNumberValue =
          value.poNumber && value.poNumber !== DEFAULT_EMPTY_VALUE
            ? value.poNumber
            : "";
        const editShipToDisabled =
          shipToSelectOptions.length <= 1 ||
          value.mode?.name !== "Truck" ||
          value.salesTermsCode !== "FOB";
        const retValue = {
          ...value,
          shipped: setShippingStatusTextColor(value.shippingStatus),
          mode: valueOr(value.mode?.name),
          releaseNumber: value.notShipped ? (
            value.releaseNumber
          ) : (
            <FileDownloadLink
              label={value.releaseNumber as number}
              onFetchFiles={mutateAsync}
              docEntry={value.docEntry}
            />
          ),
        };
        return inEditMode
          ? {
              ...retValue,
              edit: (
                <ButtonGroup
                  flexDirection={headingDirection}
                  spacing={0}
                  w="100%"
                  pl={1}
                >
                  <SubmitButton
                    isLoading={isSubmitting}
                    isDisabled={!isDirty}
                    minW="29px"
                    variant={saveButtonVariant}
                  >
                    Save
                  </SubmitButton>
                  <CancelButtonAlert
                    onCancel={closeEditRow}
                    displayAlert={isDirty}
                    isDisabled={isSubmitting}
                    w="100%"
                  />
                </ButtonGroup>
              ),
              shipTo: (
                <>
                  <SelectFormInput
                    id="shipTo"
                    options={shipToSelectOptions}
                    isInline={true}
                    isLoading={ValidShipToIsLoading}
                    isDisabled={editShipToDisabled}
                    isClearable={false}
                    w={editOrderInputWidth}
                    size="sm"
                  />
                  <TextFormInput
                    id="docEntry"
                    value={value.docEntry}
                    type="hidden"
                  />
                  <TextFormInput
                    id="isDraft"
                    value={value.isDraft === true ? "true" : "false"}
                    type="hidden"
                  />
                </>
              ),
              poNumber: (
                <TextFormInput
                  id="poNumber"
                  value={editPoNumberValue}
                  isInline={true}
                  w={editOrderInputWidth}
                />
              ),
            }
          : {
              ...retValue,
              edit: canEdit && (
                <EditButton
                  onClick={() => {
                    setRowData(value);
                    reset({
                      docEntry: value.docEntry,
                      isDraft: value.isDraft as string,
                      poNumber: editPoNumberValue as string,
                      shipTo: {
                        value: value.shipTo,
                        label: value.shipTo,
                      },
                    });
                  }}
                />
              ),
              shipTo: <Tooltip label={value.shipTo}>{value.shipTo}</Tooltip>,
              poNumber: (
                <Tooltip label={value.poNumber as string}>
                  {value.poNumber as string}
                </Tooltip>
              ),
            };
      }) ?? [],
    [
      orders,
      hasEditOrderPermission,
      rowData?.docEntry,
      shipToSelectOptions,
      mutateAsync,
      isSubmitting,
      isDirty,
      closeEditRow,
      ValidShipToIsLoading,
      reset,
    ]
  );

  return useMemo(() => {
    const emptyOnClick = hasSelectedFilters ? clearFilters : clearSearchTerm;
    const emptyButtonText = `Clear ${
      hasSelectedFilters ? "all filters" : "search"
    }`;

    return orders.length > 0 ? (
      <BackToTopFAB isVisible={orders.length > BACK_TO_TOP_MIN_LINES}>
        <OrdersTable orders={ordersTableEntries} />
      </BackToTopFAB>
    ) : (
      <EmptyWithClearButton
        message="No orders yet"
        hasFilters={hasSelectedFilters}
        hasSearch={!!searchTerm}
        onClick={emptyOnClick}
        buttonText={emptyButtonText}
        buttonVisible={hasSelectedFilters || !!searchTerm}
      />
    );
  }, [
    orders,
    hasSelectedFilters,
    clearFilters,
    clearSearchTerm,
    ordersTableEntries,
    searchTerm,
  ]);
};

export default OrdersList;
